import { alpha, Theme } from '@mui/material'
import { yellow } from '@mui/material/colors'

/**
 * Every pill on a rollup config card is the same shape in the design — rounded
 * to a capsule, with a tinted fill and a border in the same hue — varying only
 * the hue and the border style:
 *
 * - amber, solid border  → the card has unsaved work (draft or dirty)
 * - amber, dashed border → the config still needs a vendor before it can save
 * - yellow, solid border → a vendor on this card is excluded elsewhere
 * - primary, solid       → the config applies to every vendor
 * - neutral, no border   → the config is simply disabled
 *
 * They live together so they can't drift into five unrelated looks again.
 *
 * **Sizing is driven by padding, not height.** The design specifies each pill
 * as padding around its text, and the height falls out of that. Setting an
 * explicit height instead leaves MUI's own label padding in place, which is
 * wider than the design at every size and produces pills that are
 * simultaneously too short and too wide. So each of these sets `height: auto`
 * and pads the label; where a pill has a Chip `icon`, the icon is a sibling of
 * the label rather than inside it, so the left inset and the icon-to-text gap
 * come from the icon's margins.
 *
 * The design doc is dark-only, so colours derive from the palette rather than
 * copying its hex values — the pills have to hold up in light mode too. The
 * one exception is the excluded hue: the design deliberately separates
 * "excluded" from "unsaved" by using yellow against amber, and the palette has
 * no second warning tone, so it comes from MUI's yellow ramp (its dark stop is
 * the design's colour exactly) with a darker stop in light mode for contrast.
 */

const excludedHue = (theme: Theme) =>
  theme.palette.mode === 'dark' ? yellow[600] : yellow[900]

/** Capsule that sizes to its content, so the padding below is what's visible. */
const capsule = {
  height: 'auto',
  borderRadius: '999px',
  '& .MuiChip-label': { lineHeight: 1.2 },
} as const

/** Amber pill with a leading dot — "Draft — never saved" / "Unsaved changes".
 * Design: 12px/500, padding 4px 11px, 6px gap after the dot. */
export const draftPillSx = (theme: Theme) => ({
  ...capsule,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  color: theme.palette.warning.main,
  bgcolor: alpha(theme.palette.warning.main, 0.1),
  border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
  '& .MuiChip-icon': { color: 'inherit', ml: '11px', mr: '6px' },
  '& .MuiChip-label': {
    ...capsule['& .MuiChip-label'],
    pl: 0,
    pr: '11px',
    py: '4px',
  },
})

/** The dot itself, passed as the draft pill's icon. */
export const draftPillDotSx = (theme: Theme) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: theme.palette.warning.main,
})

/** Quiet neutral pill — the config is disabled. No border, unlike the others:
 * being switched off is a state, not something demanding attention.
 * Design: 12px, padding 4px 11px. */
export const disabledPillSx = (theme: Theme) => ({
  ...capsule,
  fontSize: theme.typography.pxToRem(12),
  color: theme.palette.text.secondary,
  bgcolor: alpha(theme.palette.text.primary, 0.06),
  border: 'none',
  '& .MuiChip-label': { ...capsule['& .MuiChip-label'], px: '11px', py: '4px' },
})

/** Primary pill — this config covers every vendor for the company.
 * Design: 13px/500, padding 5px 13px. */
export const allVendorsPillSx = (theme: Theme) => ({
  ...capsule,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 500,
  color: theme.palette.primary.main,
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
  '& .MuiChip-label': { ...capsule['& .MuiChip-label'], px: '13px', py: '5px' },
})

/** Dashed amber pill — this config can't be saved until it has a vendor. The
 * dash is the design's cue that the slot is empty rather than wrong.
 * Design: 12.5px, padding 5px 13px, 7px gap after the icon. */
export const vendorRequiredPillSx = (theme: Theme) => ({
  ...capsule,
  fontSize: theme.typography.pxToRem(12.5),
  color: theme.palette.warning.main,
  bgcolor: 'transparent',
  border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
  '& .MuiChip-icon': { color: 'inherit', fontSize: 14, ml: '13px', mr: '7px' },
  '& .MuiChip-label': {
    ...capsule['& .MuiChip-label'],
    pl: 0,
    pr: '13px',
    py: '5px',
  },
})

/** A vendor chip. Excluded ones take the yellow treatment; the rest are a
 * plain filled chip, which is what the design draws (grey fill, no border).
 * Design: 13px, padding 5px 8px 5px 13px — tighter on the right, where the
 * remove control sits. Its icon and remove button are rendered inside the
 * label, so the label's own padding is the whole inset. */
export const vendorChipSx = (theme: Theme, isExcluded: boolean) => {
  const base = {
    ...capsule,
    fontSize: theme.typography.pxToRem(13),
    '& .MuiChip-label': {
      ...capsule['& .MuiChip-label'],
      pl: '13px',
      pr: '8px',
      py: '5px',
    },
  }
  if (!isExcluded) return base

  const hue = excludedHue(theme)
  return {
    ...base,
    color: hue,
    bgcolor: alpha(hue, 0.08),
    border: `1px solid ${alpha(hue, 0.5)}`,
  }
}

/** Gap between a vendor chip's icon, label and remove control. */
export const VENDOR_CHIP_GAP = '7px'

/** The add-vendor control. A real button, not a clickable Chip, so `disabled`
 * genuinely removes it from the tab order — but sized to sit flush with the
 * chips beside it. Design: 12px/500, padding 5px 12px, 0.3px tracking. */
export const addVendorButtonSx = (theme: Theme) => ({
  minWidth: 0,
  py: '5px',
  px: '12px',
  borderRadius: '999px',
  borderStyle: 'dashed',
  lineHeight: 1.2,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  letterSpacing: '0.3px',
})

/** Card outline while there's unsaved work. The design tints it rather than
 * using full-strength amber — the pill is the signal, the border is support. */
export const unsavedCardBorderColor = (theme: Theme) =>
  alpha(theme.palette.warning.main, 0.4)
